import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer } from '@components/drawer';
import { ConfirmModalV2 } from '@components/modals';
import {
  ADMISSION_DRAWER_OVERLAY_Z_INDEX,
  ADMISSION_DRAWER_SURFACE_CLASSNAME,
  ADMISSION_DRAWER_WIDTH_CLASSES,
} from '@constants/admission-drawer';

import { type OfferFormDrawerProps } from './offer-form-drawer.type';

// Shared drawer shell for the offer chassis forms — owns the unsaved-changes guard + discard-confirm;
// the form is the render-prop child, wired to requestClose / setHasUnsavedChanges.
export const OfferFormDrawer = ({
  isOpen,
  onClose,
  onAfterClose,
  isSaving = false,
  isEditing,
  testId,
  children,
}: OfferFormDrawerProps): JSX.Element => {
  const { t } = useTranslation('offers');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);

  const requestClose = () => {
    if (isSaving || isDiscardConfirmOpen) return;
    if (hasUnsavedChanges) {
      setIsDiscardConfirmOpen(true);
      return;
    }
    onClose();
  };

  const handleDiscardConfirm = () => {
    setIsDiscardConfirmOpen(false);
    onClose();
  };

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={requestClose}
        onAfterClose={onAfterClose}
        position='right'
        bodyScrollable={false}
        closeOnBackdropClick={!isSaving}
        className={ADMISSION_DRAWER_WIDTH_CLASSES}>
        <div data-testid={testId} className='h-full'>
          {children({ requestClose, setHasUnsavedChanges })}
        </div>
      </Drawer>

      <ConfirmModalV2
        isOpen={isDiscardConfirmOpen}
        layout='dialog'
        type='warning'
        title={t('offers:admissionModalConfirmTitle')}
        description={
          isEditing
            ? t('offers:admissionEditModalConfirmDescription')
            : t('offers:admissionCreationModalConfirmDescription')
        }
        onConfirm={handleDiscardConfirm}
        onCancel={() => setIsDiscardConfirmOpen(false)}
        surfaceClassName={ADMISSION_DRAWER_SURFACE_CLASSNAME}
        zIndex={ADMISSION_DRAWER_OVERLAY_Z_INDEX}
      />
    </>
  );
};
